import { useState, useEffect, useCallback } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';

export interface QuotaBalance {
  period_start: string;
  period_end: string;
  plan_prs: number;
  topup_prs: number;
  used_prs: number;
  remaining_prs: number;
  plan_sast_scans: number;
  topup_sast_scans: number;
  used_sast_scans: number;
  remaining_sast_scans: number;
  plan_deep_scans: number;
  topup_deep_scans: number;
  used_deep_scans: number;
  remaining_deep_scans: number;
  max_applications: number | null;
  current_applications: number;
}

interface UseQuotaBalanceReturn {
  quotaBalance: QuotaBalance | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useQuotaBalance = (): UseQuotaBalanceReturn => {
  const api = useAuthenticatedApi();
  const { isAuthenticated, isLoading } = useAuth();
  const { currentOrganization } = useOrganization();
  const [quotaBalance, setQuotaBalance] = useState<QuotaBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const fetchBalance = async () => {
      if (isLoading || !isAuthenticated || !currentOrganization?.id || !api) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await api.get<QuotaBalance>(apiEndpoints.billing.quotaBalance);

        if (!isCancelled) {
          setQuotaBalance(response);
          setLoading(false);
        }
      } catch (err: any) {
        if (!isCancelled) {
          setError(err.message || 'Failed to fetch quota balance');
          setQuotaBalance(null);
          setLoading(false);
        }
      }
    };

    fetchBalance();
    return () => {
      isCancelled = true;
    };
  }, [api, isLoading, isAuthenticated, currentOrganization?.id, fetchTrigger]);

  return { quotaBalance, loading, error, refetch };
};
